// @flow
import { getIn, useFormikContext } from 'formik';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  DEFAULT_VALUE_FOR_DB_ROW_ID_STR,
  NATIVE_INFERENCE_POOL_TOKEN_BASED_MODELS_JSON_CONFIG,
} from '../../../constants';
import { ConfirmModal } from '../../presentational/modals/ConfirmModal';
import { ExclusiveAccordionsList } from '../../presentational/accordions/ExclusiveAccordionsList';
import { getAccordianPanelDetailsObject } from '../../../common-utils/utils';
import { StyledLabel } from '../../../common-utils/styledComponents';
import { DeleteButton } from '../../presentational/buttons/DeleteButton';
import { PaddedAndRaisedSegment } from '../../presentational/PaddedAndRaisedSegment';
import { useDeleteNativeInferencePoolTokenModelMutation } from '../../../service-definitions/estimates';
import { useEstimateVersion } from '../../../custom-hooks/useEstimateVersion';
import { toastError, toastSuccess } from '../../presentational/notifications/utils';
import { NegativeMessageContainer } from '../../presentational/messages/NegativeMessageContainer';

import { NativeInferencePoolTokenBasedModelMonthlyInputContainer } from './NativeInferencePoolTokenBasedModelMonthlyInputContainer';
import {
  getNativeInferencePoolTokenBasedModelsData,
  isAnyOtherNativeInferencePoolTokenModelBeingEdited,
} from './utils';

const getIfModelNeedsToBeDisabled = (disabledFromParent, isAnyOtherModelBeingEditedCurrently) => {
  return disabledFromParent || isAnyOtherModelBeingEditedCurrently;
};

const getModelAccordionPanels = ({
  modelsData,
  reverseModelsData,
  initialValues,
  values,
  errors,
  disabled,
  setDeleteModelModalOpen,
  setSelectedModel,
}) => {
  const accordionPanels = [];

  reverseModelsData.forEach((model, modelIndex, arr) => {
    const actualIndexOfModelInArr = arr.length - 1 - modelIndex;

    const isAnyOtherModelBeingEditedCurrently = isAnyOtherNativeInferencePoolTokenModelBeingEdited(
      modelsData.length,
      initialValues,
      values,
      actualIndexOfModelInArr
    );

    const shouldModelBeDisabled = getIfModelNeedsToBeDisabled(
      disabled,
      isAnyOtherModelBeingEditedCurrently
    );

    const keyNameInValuesToUseForThisModel = `${NATIVE_INFERENCE_POOL_TOKEN_BASED_MODELS_JSON_CONFIG}.models[${actualIndexOfModelInArr}]`;
    const error = getIn(errors, keyNameInValuesToUseForThisModel);

    accordionPanels.push(
      getAccordianPanelDetailsObject(
        <>
          <StyledLabel basic={true} content={model.modelLabel} fontSize="1.1em" />
          <DeleteButton
            disabled={shouldModelBeDisabled}
            onClick={() => {
              setSelectedModel({ model, modelIndexToRemove: actualIndexOfModelInArr });
              setDeleteModelModalOpen(true);
            }}
          />
          {error && <NegativeMessageContainer header={error?.modelName ?? ''} />}
        </>,
        <PaddedAndRaisedSegment>
          <NativeInferencePoolTokenBasedModelMonthlyInputContainer
            disabled={shouldModelBeDisabled}
            key={`${modelIndex}--${model.modelName}`}
            model={model}
            modelIndex={actualIndexOfModelInArr}
          />
        </PaddedAndRaisedSegment>,
        `${modelIndex}--${model.modelName}`
      )
    );
  });

  return accordionPanels;
};

export const NativeInferencePoolTokenBasedModelsMonthlyInputContainerWrapper = ({
  arrayHelpers,
  disabled,
}) => {
  const { estimateId, nativeInferencePoolId } = useParams();
  const [isDeleteModelModalOpen, setDeleteModelModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [deleteNativeInferencePoolTokenModel] = useDeleteNativeInferencePoolTokenModelMutation();
  const estimateVersion = useEstimateVersion();
  const { values, initialValues, errors } = useFormikContext();
  const modelsData = getNativeInferencePoolTokenBasedModelsData(values);
  const reverseModelsData = [...modelsData].reverse();

  const accordionPanels = getModelAccordionPanels({
    modelsData,
    reverseModelsData,
    initialValues,
    values,
    errors,
    disabled,
    setDeleteModelModalOpen,
    setSelectedModel,
  });

  return (
    <>
      <ExclusiveAccordionsList panels={accordionPanels} />
      {selectedModel && (
        <ConfirmModal
          body={<p>Are you sure you want to delete this Model?</p>}
          header={`Delete Model ${selectedModel?.model?.modelLabel}`}
          isOpen={isDeleteModelModalOpen}
          onClickHandlerForCancel={() => setDeleteModelModalOpen(false)}
          onClickHandlerForOK={async () => {
            const modelId = selectedModel?.model?.modelId;

            if (modelId === DEFAULT_VALUE_FOR_DB_ROW_ID_STR) {
              arrayHelpers.remove(selectedModel?.modelIndexToRemove);
              setDeleteModelModalOpen(false);
            } else {
              const payload = {
                estimate_id: estimateId,
                native_inference_pool_id: nativeInferencePoolId,
                estimate_version: estimateVersion,
                model_id: modelId,
              };

              const { error } = await deleteNativeInferencePoolTokenModel({
                estimateId,
                nativeInferencePoolId,
                modelId,
                payload,
              });

              if (error) {
                toastError(error);
              } else {
                toastSuccess('Successfully deleted the Model!');
                setDeleteModelModalOpen(false);
              }
            }
          }}
        />
      )}
    </>
  );
};
